/**
 * Stage 8–9 mastery corpus.
 *
 * These are compact, complete pedagogical studies rather than misleading
 * abridgements presented as full historical scores. Public-domain themes are
 * identified as theme studies; original miniatures are marked completeWork.
 */

import type {
  ArticulationId,
  HandId,
  MusicPiece,
  NoteEvent,
} from "./types.js";

function seq(
  midis: number[],
  step = 0.5,
  start = 0,
  hand: HandId = "right",
  velocity = 90,
  articulation?: ArticulationId,
): NoteEvent[] {
  return midis.map((midi, i) => ({
    beat: start + i * step,
    duration: articulation === "staccato" ? step * 0.45 : step * 0.9,
    midi,
    velocity,
    hand,
    articulation,
  }));
}

function line(
  events: Array<[number, number, number, number?]>,
  hand: HandId = "right",
  velocity = 90,
  articulation?: ArticulationId,
): NoteEvent[] {
  return events.map(([beat, duration, midi, eventVelocity]) => ({
    beat,
    duration,
    midi,
    velocity: eventVelocity ?? velocity,
    hand,
    articulation,
  }));
}

function scale(root: number, intervals: number[], step = 0.25, start = 0): NoteEvent[] {
  const up = intervals.map((n) => root + n);
  return seq([...up, ...up.slice(0, -1).reverse()], step, start);
}

function chord(midis: number[], beats: number[], duration = 1, velocity = 90): NoteEvent[] {
  return beats.flatMap((beat) =>
    midis.map((midi) => ({ beat, duration, midi, velocity, hand: "both" as const })),
  );
}

function piece(p: MusicPiece): MusicPiece {
  return p;
}

const MAJOR = [0, 2, 4, 5, 7, 9, 11, 12];
const NAT_MINOR = [0, 2, 3, 5, 7, 8, 10, 12];
const HARM_MINOR = [0, 2, 3, 5, 7, 8, 11, 12];
const MELODIC_MINOR = [0, 2, 3, 5, 7, 9, 11, 12];

function scaleSet(roots: number[], pattern: number[]): NoteEvent[] {
  return roots.flatMap((root, i) => scale(root, pattern, 0.25, i * 4));
}

/** Two voices played together at matching beats. */
function paired(
  upper: number[],
  lower: number[],
  step = 0.5,
  velocityUpper = 94,
  velocityLower = 74,
): NoteEvent[] {
  const notes: NoteEvent[] = [];
  const n = Math.min(upper.length, lower.length);
  for (let i = 0; i < n; i++) {
    notes.push({
      beat: i * step,
      duration: step * 0.9,
      midi: lower[i],
      velocity: velocityLower,
      hand: "left",
    });
    notes.push({
      beat: i * step,
      duration: step * 0.9,
      midi: upper[i],
      velocity: velocityUpper,
      hand: "right",
    });
  }
  return notes;
}

export const MASTERY_PIECES: MusicPiece[] = [
  piece({
    id: "notation-rests-dots-ties",
    title: "Rests, Dots & Ties Reading Study",
    category: "theory",
    bpm: 68,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 8,
    description: "Gapped attacks encode rests; long values train dots and ties.",
    skills: ["rests", "dotted notes", "ties", "duration reading"],
    notes: line([
      [0, 1.5, 60],
      [2, 0.5, 62],
      [3, 1, 64],
      [5, 2, 65],
      [8, 0.5, 67],
      [9, 1.5, 69],
      [11, 1, 67],
      [13, 3, 60],
    ]),
  }),
  piece({
    id: "notation-articulation-study",
    title: "Slur, Staccato, Tenuto & Accent Study",
    category: "theory",
    bpm: 72,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 8,
    description: "Contrasting touch groups with written articulation intent.",
    skills: ["slurs", "staccato", "tenuto", "accents"],
    notes: [
      ...seq([60, 62, 64, 65], 0.5, 0, "right", 82, "legato"),
      ...seq([67, 65, 64, 62], 0.5, 2, "right", 82, "staccato"),
      ...seq([60, 64, 67, 64], 1, 4, "right", 88, "tenuto"),
      ...seq([60, 62, 64, 67], 1, 8, "right", 112, "accent"),
    ],
  }),
  piece({
    id: "notation-ledger-key-signatures",
    title: "Ledger Lines & Key-Signature Reading",
    category: "sight",
    bpm: 66,
    timeSignature: "4/4",
    key: "D major",
    hand: "both",
    stage: 8,
    description: "Bass/treble span with F-sharp and C-sharp.",
    skills: ["bass clef", "treble clef", "ledger lines", "key signatures"],
    notes: paired(
      [62, 66, 69, 74, 78, 81, 78, 74, 69, 66, 62],
      [38, 42, 45, 50, 54, 57, 54, 50, 45, 42, 38],
      0.75,
    ),
  }),
  piece({
    id: "rhythm-compound-meters",
    title: "Compound Meter 6/8 Study",
    category: "theory",
    bpm: 72,
    timeSignature: "6/8",
    key: "A minor",
    hand: "right",
    stage: 8,
    description: "Two large pulses, each divided into three eighth notes.",
    skills: ["6/8", "compound meter", "dotted-quarter pulse"],
    notes: seq([69, 72, 76, 72, 71, 74, 77, 74, 69, 72, 76, 81], 0.5),
  }),
  piece({
    id: "sight-unseen-a",
    title: "Unseen Sight Line A",
    category: "sight",
    bpm: 70,
    timeSignature: "3/4",
    key: "F major",
    hand: "right",
    stage: 8,
    description: "Fresh step/skip line; no memorized melody.",
    skills: ["sight reading", "3/4", "B-flat"],
    notes: seq([65, 69, 67, 70, 69, 65, 67, 72, 70, 67, 69, 65], 1),
  }),
  piece({
    id: "sight-unseen-b",
    title: "Unseen Sight Line B",
    category: "sight",
    bpm: 76,
    timeSignature: "4/4",
    key: "G major",
    hand: "both",
    stage: 8,
    description: "Two-hand reading with independent contour.",
    skills: ["sight reading", "two staves", "F-sharp"],
    notes: paired(
      [67, 69, 71, 74, 72, 71, 69, 67, 74, 72, 71, 67],
      [43, 50, 47, 50, 45, 50, 47, 43, 47, 45, 47, 43],
      1,
    ),
  }),
  piece({
    id: "ear-intervals-complete",
    title: "Interval Echo — 2nds through Octaves",
    category: "ear",
    bpm: 60,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 8,
    description: "Reference C followed by ascending and descending interval answers.",
    skills: ["interval recognition", "echo playing"],
    notes: seq([60, 62, 60, 64, 60, 65, 60, 67, 60, 69, 60, 71, 60, 72], 1),
  }),
  piece({
    id: "ear-chord-cadence-colors",
    title: "Chord Quality & Cadence Echo",
    category: "ear",
    bpm: 58,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 8,
    description: "Major, minor, diminished and V–I cadence colours.",
    skills: ["chord recognition", "cadence recognition"],
    notes: [
      ...chord([60, 64, 67], [0], 2),
      ...chord([57, 60, 64], [2], 2),
      ...chord([59, 62, 65], [4], 2),
      ...chord([55, 59, 62], [6], 2),
      ...chord([60, 64, 67], [8], 3),
    ],
  }),
  piece({
    id: "pedal-change-foundations",
    title: "Sustain Pedal — Clean Harmony Changes",
    category: "exercise",
    bpm: 60,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 8,
    description: "Pedal after attack; clear and re-catch at each harmony change.",
    skills: ["sustain pedal", "syncopated pedal", "clean changes"],
    pedalCues: [
      { beat: 0.15, down: true },
      { beat: 2, down: false },
      { beat: 2.15, down: true },
      { beat: 4, down: false },
      { beat: 4.15, down: true },
      { beat: 6, down: false },
      { beat: 6.15, down: true },
      { beat: 8, down: false },
    ],
    notes: [
      ...chord([48, 60, 64, 67], [0], 1.5),
      ...chord([53, 60, 65, 69], [2], 1.5),
      ...chord([55, 59, 62, 67], [4], 1.5),
      ...chord([48, 60, 64, 67], [6], 2),
    ],
  }),
  piece({
    id: "pedal-legato-advanced",
    title: "Pedal Legato & Half-Pedal Mindset",
    category: "etude",
    bpm: 66,
    timeSignature: "3/4",
    key: "A minor",
    hand: "both",
    stage: 8,
    description: "Melody over changing broken harmony with frequent clean pedal lifts.",
    skills: ["pedal legato", "pedal clarity", "melody balance"],
    pedalCues: [
      { beat: 0.1, down: true },
      { beat: 3, down: false },
      { beat: 3.1, down: true },
      { beat: 6, down: false },
      { beat: 6.1, down: true },
      { beat: 9, down: false },
    ],
    notes: [
      ...seq([45, 52, 57, 48, 52, 57, 43, 50, 55], 1, 0, "left", 68, "legato"),
      ...line([
        [0, 1.5, 69, 96],
        [1.5, 1.5, 72, 100],
        [3, 1, 71, 94],
        [4, 2, 69, 88],
        [6, 1.5, 67, 86],
        [7.5, 1.5, 69, 92],
      ]),
    ],
  }),
  piece({
    id: "expression-dynamics-terraces",
    title: "Dynamics: pp → mf → ff → p",
    category: "etude",
    bpm: 72,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 8,
    description: "Velocity targets grade controlled dynamic levels.",
    skills: ["dynamics", "crescendo", "decrescendo", "tone control"],
    notes: [
      ...seq([60, 62, 64, 65], 0.5, 0, "right", 45, "legato"),
      ...seq([67, 69, 71, 72], 0.5, 2, "right", 78, "legato"),
      ...seq([72, 71, 69, 67], 0.5, 4, "right", 115, "accent"),
      ...seq([65, 64, 62, 60], 0.5, 6, "right", 58, "legato"),
    ],
  }),
  piece({
    id: "expression-phrase-voicing",
    title: "Phrasing & Melody Voicing Study",
    category: "etude",
    bpm: 68,
    timeSignature: "4/4",
    key: "F major",
    hand: "both",
    stage: 8,
    description: "RH sings above a deliberately softer accompaniment.",
    skills: ["phrasing", "voicing", "balance", "breathing"],
    notes: paired(
      [69, 70, 72, 74, 72, 70, 69, 67, 69, 70, 69, 65],
      [41, 48, 45, 48, 43, 48, 45, 48, 41, 48, 45, 41],
      0.75,
      102,
      58,
    ),
  }),
  piece({
    id: "ornaments-trill-mordent-turn",
    title: "Trill, Mordent & Turn Control",
    category: "etude",
    bpm: 64,
    timeSignature: "4/4",
    key: "G major",
    hand: "right",
    stage: 8,
    description: "Measured ornament cells before free expressive placement.",
    skills: ["trill", "mordent", "turn", "ornaments"],
    notes: seq(
      [67, 69, 67, 69, 67, 66, 67, 69, 71, 69, 67, 69, 71, 72, 71, 69, 67],
      0.25,
    ),
  }),
  piece({
    id: "repertoire-baroque-two-voice",
    title: "Complete Baroque Two-Voice Miniature",
    category: "repertoire",
    bpm: 76,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 8,
    description: "Original complete miniature: imitation, independence and cadence.",
    skills: ["baroque articulation", "counterpoint", "voice independence"],
    completeWork: true,
    notes: paired(
      [60, 62, 64, 67, 65, 64, 62, 60, 64, 65, 67, 72, 71, 69, 67, 60],
      [48, 47, 45, 43, 48, 50, 52, 53, 52, 50, 48, 43, 45, 47, 43, 48],
      0.5,
      92,
      78,
    ),
  }),
  piece({
    id: "repertoire-classical-alberti",
    title: "Complete Classical Alberti Miniature",
    category: "repertoire",
    bpm: 92,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 8,
    description: "Original sonatina-style miniature with Alberti bass and balanced phrases.",
    skills: ["Alberti bass", "classical phrasing", "cadence"],
    completeWork: true,
    notes: [
      ...seq([48, 55, 52, 55, 48, 55, 52, 55, 43, 50, 47, 50, 48, 55, 52, 48], 0.5, 0, "left", 66),
      ...line([
        [0, 1, 64],
        [1, 1, 67],
        [2, 2, 72],
        [4, 1, 71],
        [5, 1, 69],
        [6, 2, 67],
        [8, 1, 65],
        [9, 1, 62],
        [10, 2, 67],
        [12, 1, 64],
        [13, 1, 62],
        [14, 2, 60],
      ], "right", 98, "legato"),
    ],
  }),
  piece({
    id: "repertoire-romantic-lyric",
    title: "Complete Romantic Lyric Miniature",
    category: "repertoire",
    bpm: 64,
    timeSignature: "3/4",
    key: "A minor",
    hand: "both",
    stage: 8,
    description: "Original complete lyrical work with rubato mindset and pedal changes.",
    skills: ["romantic phrasing", "rubato", "pedal", "voicing"],
    completeWork: true,
    pedalCues: [
      { beat: 0.1, down: true },
      { beat: 3, down: false },
      { beat: 3.1, down: true },
      { beat: 6, down: false },
      { beat: 6.1, down: true },
      { beat: 9, down: false },
      { beat: 9.1, down: true },
      { beat: 12, down: false },
    ],
    notes: [
      ...seq([45, 52, 57, 48, 52, 57, 43, 50, 55, 45, 52, 57], 1, 0, "left", 58, "legato"),
      ...line([
        [0, 1.5, 69, 98],
        [1.5, 1.5, 72, 104],
        [3, 1, 71, 96],
        [4, 2, 69, 88],
        [6, 1.5, 67, 85],
        [7.5, 1.5, 69, 92],
        [9, 1, 71, 96],
        [10, 2, 69, 82],
      ]),
    ],
  }),
  piece({
    id: "tech-major-scales-sharp-keys",
    title: "Major Scales — C G D A E B",
    category: "scale",
    bpm: 88,
    timeSignature: "4/4",
    key: "Multiple major keys",
    hand: "right",
    stage: 9,
    description: "Six major keys in one continuous technical circuit.",
    skills: ["all major scales", "sharp keys", "fingering"],
    notes: scaleSet([60, 55, 50, 57, 52, 59], MAJOR),
  }),
  piece({
    id: "tech-major-scales-flat-keys",
    title: "Major Scales — F B♭ E♭ A♭ D♭ G♭",
    category: "scale",
    bpm: 84,
    timeSignature: "4/4",
    key: "Multiple major keys",
    hand: "right",
    stage: 9,
    description: "Six flat-side major keys in one circuit.",
    skills: ["all major scales", "flat keys", "fingering"],
    notes: scaleSet([53, 58, 63, 56, 61, 54], MAJOR),
  }),
  piece({
    id: "tech-minor-scales-natural",
    title: "Natural Minor Scale Circuit — 12 Keys",
    category: "scale",
    bpm: 82,
    timeSignature: "4/4",
    key: "All minor keys",
    hand: "right",
    stage: 9,
    description: "Twelve natural minor scales, one octave each.",
    skills: ["all minor scales", "natural minor"],
    notes: scaleSet([57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68], NAT_MINOR),
  }),
  piece({
    id: "tech-minor-scales-harmonic",
    title: "Harmonic Minor Scale Circuit — 12 Keys",
    category: "scale",
    bpm: 78,
    timeSignature: "4/4",
    key: "All minor keys",
    hand: "right",
    stage: 9,
    description: "Raised leading tone in every minor key.",
    skills: ["harmonic minor", "leading tone", "all keys"],
    notes: scaleSet([57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68], HARM_MINOR),
  }),
  piece({
    id: "tech-minor-scales-melodic",
    title: "Melodic Minor Scale Circuit — 12 Keys",
    category: "scale",
    bpm: 78,
    timeSignature: "4/4",
    key: "All minor keys",
    hand: "right",
    stage: 9,
    description: "Raised 6th/7th ascending melodic-minor control.",
    skills: ["melodic minor", "all keys", "fingering"],
    notes: scaleSet([57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68], MELODIC_MINOR),
  }),
  piece({
    id: "tech-arpeggios-all-keys",
    title: "Major & Minor Arpeggio Circuit",
    category: "arpeggio",
    bpm: 84,
    timeSignature: "4/4",
    key: "All keys",
    hand: "right",
    stage: 9,
    description: "Major then minor arpeggios around the chromatic circle.",
    skills: ["arpeggios", "thumb crossings", "all keys"],
    notes: Array.from({ length: 12 }, (_, i) => 48 + i).flatMap((root, i) => {
      const major = scale(root, [0, 4, 7, 12], 0.25, i * 4);
      const minor = scale(root, [0, 3, 7, 12], 0.25, i * 4 + 2);
      return [...major, ...minor];
    }),
  }),
  piece({
    id: "tech-chromatic-double-thirds",
    title: "Chromatic Control & Broken Thirds",
    category: "etude",
    bpm: 92,
    timeSignature: "4/4",
    key: "Chromatic",
    hand: "right",
    stage: 9,
    description: "Chromatic fingering alternates with diatonic broken thirds.",
    skills: ["chromatic scale", "thirds", "finger substitution"],
    notes: [
      ...seq([60, 61, 62, 63, 64, 65, 66, 67, 66, 65, 64, 63, 62, 61, 60], 0.25),
      ...seq([60, 64, 62, 65, 64, 67, 65, 69, 67, 71, 69, 72], 0.25, 4),
    ],
  }),
  piece({
    id: "tech-hanon-pattern-original",
    title: "Five-Finger Independence Circuit",
    category: "etude",
    bpm: 96,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 9,
    description: "Original non-copyrighted Hanon-style transposition circuit.",
    skills: ["finger independence", "evenness", "transposition"],
    notes: paired(
      [60, 62, 64, 65, 67, 65, 64, 62, 62, 64, 65, 67, 69, 67, 65, 64],
      [48, 50, 52, 53, 55, 53, 52, 50, 50, 52, 53, 55, 57, 55, 53, 52],
      0.25,
      90,
      86,
    ),
  }),
  piece({
    id: "tech-czerny-velocity-original",
    title: "Classical Velocity Etude",
    category: "etude",
    bpm: 108,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 9,
    description: "Original Czerny-style passagework with clear even attacks.",
    skills: ["velocity", "rotation", "even tone", "two-hand coordination"],
    notes: paired(
      [60, 64, 67, 72, 67, 64, 62, 65, 69, 74, 69, 65, 64, 67, 71, 76],
      [48, 52, 55, 60, 55, 52, 50, 53, 57, 62, 57, 53, 52, 55, 59, 64],
      0.25,
      96,
      82,
    ),
  }),
  piece({
    id: "sight-transposition-challenge",
    title: "Advanced Unseen Transposition Line",
    category: "sight",
    bpm: 80,
    timeSignature: "4/4",
    key: "E-flat major",
    hand: "both",
    stage: 9,
    description: "Read an unfamiliar two-staff line in a flat key.",
    skills: ["advanced sight reading", "transposition", "E-flat major"],
    notes: paired(
      [63, 65, 67, 70, 68, 67, 65, 63, 70, 72, 74, 75],
      [39, 46, 43, 46, 41, 48, 43, 39, 46, 43, 46, 39],
      0.75,
    ),
  }),
  piece({
    id: "ear-transcription-advanced",
    title: "Advanced Call-and-Response Transcription",
    category: "ear",
    bpm: 68,
    timeSignature: "4/4",
    key: "D minor",
    hand: "right",
    stage: 9,
    description: "Longer phrase with leaps, sequence and harmonic-minor colour.",
    skills: ["melodic dictation", "memory", "intervals"],
    notes: seq([62, 65, 69, 68, 69, 72, 70, 69, 65, 67, 64, 62], 0.75),
  }),
  piece({
    id: "improv-motif-variation",
    title: "Motif Variation Framework",
    category: "theory",
    bpm: 76,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 9,
    description: "Model motif, sequence, inversion-like contour and cadence.",
    skills: ["improvisation", "motif development", "sequence"],
    notes: [
      ...seq([60, 62, 64, 62, 62, 64, 65, 64, 67, 65, 64, 62], 0.5, 0, "right"),
      ...seq([48, 55, 50, 55, 52, 55], 1, 0, "left", 68),
    ],
  }),
  piece({
    id: "theory-harmony-composition",
    title: "Composition Lab — Phrase & Cadence",
    category: "theory",
    bpm: 70,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 9,
    description: "Question/answer phrase over I–IV–V–I harmony.",
    skills: ["composition", "harmonic function", "phrase structure", "cadence"],
    notes: [
      ...chord([48, 60, 64, 67], [0], 2, 70),
      ...chord([53, 60, 65, 69], [2], 2, 70),
      ...chord([55, 59, 62, 67], [4], 2, 74),
      ...chord([48, 60, 64, 67], [6], 2, 68),
      ...seq([64, 65, 67, 72, 71, 69, 67, 60], 1, 0, "right", 96),
    ],
  }),
  piece({
    id: "performance-recital-a",
    title: "Recital I — Baroque & Classical",
    category: "repertoire",
    bpm: 84,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 9,
    description: "Complete scored recital study: clarity, balance and continuity.",
    skills: ["performance", "continuity", "recovery", "style"],
    completeWork: true,
    notes: paired(
      [60, 64, 67, 72, 71, 69, 67, 65, 64, 62, 64, 67, 65, 62, 60, 60],
      [48, 52, 55, 48, 43, 47, 50, 53, 48, 55, 52, 48, 53, 55, 48, 48],
      0.5,
      98,
      72,
    ),
  }),
  piece({
    id: "performance-recital-b",
    title: "Recital II — Romantic Expression",
    category: "repertoire",
    bpm: 66,
    timeSignature: "3/4",
    key: "E minor",
    hand: "both",
    stage: 9,
    description: "Complete scored recital study: melody voicing, pedal and dynamic arc.",
    skills: ["performance", "pedal", "voicing", "dynamic arc"],
    completeWork: true,
    pedalCues: [
      { beat: 0.1, down: true },
      { beat: 3, down: false },
      { beat: 3.1, down: true },
      { beat: 6, down: false },
      { beat: 6.1, down: true },
      { beat: 9, down: false },
      { beat: 9.1, down: true },
      { beat: 12, down: false },
    ],
    notes: paired(
      [64, 67, 71, 74, 72, 71, 67, 69, 71, 76, 74, 71, 67, 66, 64, 64],
      [40, 47, 43, 47, 45, 52, 47, 52, 40, 47, 43, 47, 45, 47, 40, 40],
      0.75,
      102,
      60,
    ),
  }),
  piece({
    id: "performance-final-capstone",
    title: "Final Recital Capstone",
    category: "repertoire",
    bpm: 88,
    timeSignature: "4/4",
    key: "D major",
    hand: "both",
    stage: 9,
    description: "Complete final work combining scales, chords, independence and expression.",
    skills: ["final recital", "technique", "musicianship", "performance"],
    completeWork: true,
    notes: paired(
      [62, 66, 69, 74, 73, 74, 69, 66, 64, 67, 71, 76, 74, 71, 69, 66, 62, 64, 66, 69, 67, 66, 64, 62],
      [38, 45, 42, 45, 40, 47, 42, 45, 43, 50, 47, 50, 38, 45, 42, 45, 43, 47, 45, 42, 40, 45, 38, 38],
      0.5,
      100,
      70,
    ),
  }),
];
